import {
    Availability,
    AvailabilityStatus,
    dateAdd,
    formatDateShort,
    formatWeekDayShort,
    getDayOfWeek,
    getHolidayForDate,
    getRange,
    type Holiday,
    parseDateString,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { alert } from "../alert-dialog";
import "../availability-dialog";
import type { AvailabilityDialog } from "../availability-dialog";
import "../date-picker";
import "../popover";
import type { Popover } from "../popover";
import "../scroller";

type DayData = { date: DateString; isSunday: boolean; holiday: Holiday | null; availabilities: Availability[] };

@customElement("ptc-staff-availabilities")
export class Availabilities extends Routing(StateMixin(LitElement)) {
    routePattern = /^(home|availabilities)/;

    async connectedCallback() {
        super.connectedCallback();
        await app.loaded;
        if (app.account) {
            void this._load();
        }
    }

    @property({ reflect: true, type: Boolean })
    expanded = false;

    @property({ attribute: false })
    date: DateString;

    @state()
    private _loading = false;

    @state()
    private _availabilities: Availability[] = [];

    @state()
    private _days: DayData[] = [];

    @singleton("ptc-availability-dialog")
    private _availabilityDialog: AvailabilityDialog;

    private get _employee() {
        return app.profile;
    }

    private get _range() {
        if (!this.date || !parseDateString(this.date)) {
            return null;
        }
        return getRange(this.date, "week");
    }

    refresh() {
        return this._load();
    }

    async updated(changes: Map<string, unknown>) {
        if (changes.has("date")) {
            await this._load();
        }
    }

    private async _load() {
        if (this._loading) return;

        if (!this._employee) {
            return;
        }

        const range = this._range;
        if (!range) {
            void alert("Ungültiges Datum", { type: "warning" });
            return;
        }

        const { from, to } = range;
        this._loading = true;

        try {
            this._availabilities = await app.api.getAvailabilities({ employees: [this._employee.id], from, to });

            const days: DayData[] = [];
            let date = from;
            while (date < to) {
                days.push({
                    date,
                    isSunday: getDayOfWeek(date) === 0,
                    holiday: getHolidayForDate(date, {
                        holidays: app.venues[0].enabledHolidays,
                        country: app.company!.country,
                    }),
                    availabilities: this._availabilities.filter((a) => a.date === date),
                });
                date = dateAdd(date, { days: 1 });
            }
            this._days = days;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _newAvailability(date: DateString) {
        const created = await this._availabilityDialog.show(
            new Availability({
                employeeId: this._employee!.id,
                status: AvailabilityStatus.Available,
                date,
            })
        );
        if (created) {
            void this._load();
        }
    }

    private async _editAvailability(av: Availability, e?: Event) {
        e?.stopPropagation();
        const edited = await this._availabilityDialog.show(av);
        if (edited) {
            void this._load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                position: relative;
            }

            .sticky {
                z-index: 3;
                left: 0;
            }

            .horizontal-view,
            .vertical-view {
                will-change: opacity;
                transition: opacity 0.3s;
            }

            :host([expanded]) .horizontal-view,
            :host(:not([expanded])) .vertical-view {
                opacity: 0;
                pointer-events: none;
            }
        `,
    ];

    private _renderAvailability(av: Availability, compact = false) {
        return html`
            <div
                class="${!compact ? "margined click" : ""} padded box horizontal center-aligning layout"
                style="--color-highlight: ${av.color}"
                @click=${!compact ? (e: Event) => this._editAvailability(av, e) : null}
            >
                <div class="stretch ellipsis">
                    <ptc-ui-icon name="${av.icon}"></ptc-ui-icon>
                    ${av.fullLabel}
                </div>
                ${av.repeats !== "never" && !compact
                    ? html`
                          <div class="smaller subtle">
                              <ptc-ui-icon name="icon.object.history"></ptc-ui-icon>
                              ${av.repeatsLabel}
                          </div>
                      `
                    : ""}
            </div>
        `;
    }

    private _renderHorizontal() {
        return html`
            <div
                class="horizontal scroller horizontal-view hide-scrollbar"
                style="margin-top: -0.5em;"
                ?hidden=${this.expanded}
            >
                <div class="margined horizontally-padded spacing start-aligning horizontal layout">
                    ${this._days.map(
                        ({ date, availabilities }) => html`
                            <div class="start-aligning vertical layout">
                                <div class="padded background blue colored-text sticky">
                                    <strong>${formatWeekDayShort(date)}</strong>, ${formatDateShort(date)}
                                </div>

                                <div class="spacing horizontal layout">
                                    ${availabilities.length
                                        ? availabilities.map((av) => this._renderAvailability(av, true))
                                        : html` <div class="padded semibold faded">Keine Angaben</div> `}
                                </div>
                            </div>
                        `
                    )}
                </div>
            </div>
        `;
    }

    private _renderVertical() {
        const emp = this._employee;
        const range = this._range;
        if (!emp || !range) {
            return;
        }

        const { from, to } = range;
        const today = toDateString(new Date());
        return html`
            <div class="fullbleed vertical layout vertical-view" ?hidden=${!this.expanded}>
                <ptc-scroller class="stretch">
                    <div class="vertical layout fill-vertically">
                        ${this._days.map(
                            ({ date, isSunday, holiday, availabilities }) => html`
                                <div
                                    class="stretch padded border-bottom click"
                                    @click=${() => this._newAvailability(date)}
                                    style="min-height: 5em"
                                    ?disabled=${date < today}
                                >
                                    <div class="padded horizontal layout">
                                        <div class="stretch ${isSunday ? "orange colored-text" : ""}">
                                            <strong>${formatWeekDayShort(date)}</strong>, ${formatDateShort(date)}
                                        </div>
                                        ${holiday ? html` <div class="violet colored-text">${holiday.name}</div> ` : ""}
                                    </div>
                                    ${availabilities.map((av) => this._renderAvailability(av))}
                                </div>
                            `
                        )}
                    </div>
                </ptc-scroller>

                <div class="padded horizontal center-aligning spacing layout">
                    <div class="horizontal layout" style="min-width: 8em;">
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: dateAdd(this.date, { days: -7 }) })}
                        >
                            <ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon>
                        </button>
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: today })}
                            ?disabled=${this.date === today}
                        >
                            <ptc-ui-icon name="icon.object.day-view"></ptc-ui-icon>
                        </button>
                    </div>
                    <div class="centering horizontal layout stretch">
                        <button class="transparent">
                            <strong>${formatDateShort(from)}</strong> -
                            <strong>${formatDateShort(dateAdd(to, { days: -1 }))}</strong> ${parseDateString(
                                this.date
                            )?.getFullYear()}
                        </button>
                        <ptc-popover positioning="absolute" alignment="top">
                            <ptc-date-picker
                                mode="week"
                                .value=${this.date}
                                @change=${(e: CustomEvent<{ value: string }>) => {
                                    this.go(null, { date: e.detail.value });
                                    ((e.target as HTMLElement).parentElement as Popover).hide();
                                }}
                            ></ptc-date-picker>
                        </ptc-popover>
                    </div>
                    <div class="horizontal end-justifying layout" style="min-width: 8em;">
                        <button
                            class="transparent"
                            type="button"
                            @click=${() => this.go(null, { date: dateAdd(this.date, { days: 7 }) })}
                        >
                            <ptc-ui-icon name="icon.navigation.forward"></ptc-ui-icon>
                        </button>
                    </div>
                </div>
            </div>

            <div class="fullbleed center-aligning vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }

    render() {
        return html`${this._renderVertical()} ${this._renderHorizontal()}`;
    }
}
